/**
 * Admin → Workflow Engine (Session 1 landing page).
 *
 * Surfaces:
 *   - The 4 seeded Program Templates with their canonical workflow id
 *     and current state.
 *   - All workflow definitions grouped by slug with version + state.
 *   - All recent runs with status + subject.
 *
 * The visual workflow builder, sandbox, and analytics dashboards arrive
 * in subsequent Workflow Engine sessions and will attach here.
 *
 * Route: /admin/workflow-engine
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import { Activity, AlertTriangle, BarChart3, Layers, Play, Plus, RefreshCw, Sparkles, Truck, Workflow as WorkflowIcon, Zap } from "lucide-react";
import NewWorkflowModal from "./NewWorkflowModal";
import BackfillVendorsModal from "./BackfillVendorsModal";
import BatchScenariosModal from "./BatchScenariosModal";

const STATE_PALETTE = {
  draft: "bg-slate-100 text-slate-700",
  testing: "bg-violet-100 text-violet-700",
  scheduled: "bg-amber-100 text-amber-800",
  live: "bg-emerald-100 text-emerald-700",
  paused: "bg-yellow-100 text-yellow-800",
  archived: "bg-slate-100 text-slate-500",
};

const RUN_STATUS_PALETTE = {
  pending: "bg-slate-100 text-slate-700",
  running: "bg-blue-100 text-blue-700",
  paused: "bg-yellow-100 text-yellow-800",
  completed: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
  cancelled: "bg-slate-100 text-slate-500",
};

function StatePill({ value, palette = STATE_PALETTE }) {
  const cls = palette[value] || "bg-slate-100 text-slate-700";
  return (
    <span
      data-testid={`wfe-state-pill-${value}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}
    >
      {value}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

const COVERAGE_STATUS_PALETTE = {
  ok:           { bg: "bg-emerald-100 text-emerald-700", label: "ok" },
  pending:      { bg: "bg-amber-100 text-amber-700",     label: "pending" },
  rejected:     { bg: "bg-rose-100 text-rose-700",       label: "rejected" },
  missing:      { bg: "bg-rose-100 text-rose-700",       label: "missing" },
  unconfigured: { bg: "bg-slate-200 text-slate-700",     label: "unconfigured" },
};

function CoverageStatusPill({ status }) {
  const p = COVERAGE_STATUS_PALETTE[status] || COVERAGE_STATUS_PALETTE.missing;
  return (
    <span
      data-testid={`coverage-status-${status}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${p.bg}`}
    >
      {p.label}
    </span>
  );
}

function VendorCoverageSection({ coverage, drift, onOpenBackfill }) {
  const [latestOnly, setLatestOnly] = useState(true);
  const summary = coverage?.summary;
  const allWorkflowsWithRefs = useMemo(
    () => (coverage?.workflows || []).filter((w) => w.total_refs > 0),
    [coverage],
  );

  // "Latest only" collapses each slug to up to 2 rows: the live version (if
  // any) + the newest non-live version. This is the natural "what's deployed
  // vs. what's being worked on" view.
  const workflows = useMemo(() => {
    if (!latestOnly) return allWorkflowsWithRefs;
    const bySlug = new Map();
    allWorkflowsWithRefs.forEach((w) => {
      if (!bySlug.has(w.slug)) bySlug.set(w.slug, { live: null, nonLive: null });
      const bucket = bySlug.get(w.slug);
      if (w.state === "live") {
        if (!bucket.live || (w.version || 0) > (bucket.live.version || 0)) bucket.live = w;
      } else if (!bucket.nonLive || (w.version || 0) > (bucket.nonLive.version || 0)) {
        bucket.nonLive = w;
      }
    });
    const out = [];
    bySlug.forEach(({ live, nonLive }) => {
      if (live) out.push(live);
      if (nonLive) out.push(nonLive);
    });
    return out;
  }, [allWorkflowsWithRefs, latestOnly]);

  const hiddenCount = allWorkflowsWithRefs.length - workflows.length;

  return (
    <div className="mb-8" data-testid="wfe-vendor-coverage">
      <div className="flex items-center gap-2 mb-3">
        <Truck className="w-4 h-4 text-blue-700" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
          Vendor coverage map
        </h2>
        {summary && summary.broken_refs > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
            <AlertTriangle className="w-3 h-3" /> {summary.broken_refs} broken
          </span>
        )}
        {summary && summary.broken_refs > 0 && (
          <button
            type="button"
            onClick={onOpenBackfill}
            data-testid="wfe-coverage-backfill-btn"
            className="ml-auto inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-3 py-1.5 text-xs font-semibold"
          >
            <Sparkles className="w-3.5 h-3.5" /> Backfill placeholders
          </button>
        )}
      </div>

      {/* Drift status row */}
      {drift?.latest && (
        <div
          data-testid="wfe-drift-status"
          className="mb-3 flex items-center gap-2 flex-wrap text-[11px] text-slate-600"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Drift check ran {new Date(drift.latest.ran_at).toLocaleString(undefined, {month: "short", day: "numeric", hour: "numeric", minute: "2-digit"})}
          </span>
          {drift.latest.drifted?.length > 0 && (
            <span
              data-testid="wfe-drift-broken-chip"
              className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 font-bold"
            >
              <AlertTriangle className="w-3 h-3" /> {drift.latest.drifted.length} new break{drift.latest.drifted.length === 1 ? "" : "s"}
            </span>
          )}
          {drift.latest.recovered?.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-bold">
              ↺ {drift.latest.recovered.length} recovered
            </span>
          )}
          {drift.latest.emailed_to?.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 px-2 py-0.5">
              digest emailed to {drift.latest.emailed_to.length}
            </span>
          )}
          <span className="text-slate-400">· daily 04:00 UTC</span>
        </div>
      )}

      {!summary ? (
        <div className="text-xs text-slate-500 italic">Loading coverage…</div>
      ) : workflows.length === 0 ? (
        <div
          data-testid="wfe-coverage-empty"
          className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500"
        >
          No workflows reference vendors yet. Use the <strong>Dispatch to vendor</strong> action in any
          step to start populating this map.
        </div>
      ) : (
        <>
          {/* Top-level summary chips */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Active workflows</div>
              <div className="font-display text-xl font-bold text-slate-900 tabular-nums mt-0.5">
                {summary.workflows_with_vendor_refs} / {summary.total_workflows}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">touch a vendor</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total dispatches</div>
              <div className="font-display text-xl font-bold text-slate-900 tabular-nums mt-0.5">{summary.total_refs}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">across all workflows</div>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Broken refs</div>
              <div className="font-display text-xl font-bold text-rose-900 tabular-nums mt-0.5">{summary.broken_refs}</div>
              <div className="text-[10px] text-rose-600 mt-0.5">needs admin attention</div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Healthy refs</div>
              <div className="font-display text-xl font-bold text-emerald-900 tabular-nums mt-0.5">
                {summary.total_refs - summary.broken_refs}
              </div>
              <div className="text-[10px] text-emerald-600 mt-0.5">approved + wired up</div>
            </div>
          </div>

          {/* Per-workflow table */}
          <div className="flex items-center justify-between mb-2">
            <label
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer"
              data-testid="wfe-coverage-latest-only-label"
            >
              <input
                type="checkbox"
                checked={latestOnly}
                onChange={(e) => setLatestOnly(e.target.checked)}
                data-testid="wfe-coverage-latest-only"
                className="accent-blue-600"
              />
              Latest version only
            </label>
            {latestOnly && hiddenCount > 0 && (
              <span
                data-testid="wfe-coverage-hidden-count"
                className="text-[10px] text-slate-500"
              >
                {hiddenCount} older draft{hiddenCount === 1 ? "" : "s"} hidden
              </span>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="wfe-coverage-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-2">Workflow</th>
                  <th className="text-left px-3 py-2">Step</th>
                  <th className="text-left px-3 py-2">Vendor → Service</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {workflows.map((w) =>
                  w.vendor_refs.map((r, idx) => (
                    <tr
                      key={`${w.definition_id}-${r.action_id || idx}`}
                      data-testid={`coverage-row-${w.definition_id}-${r.action_id || idx}`}
                      className="border-t border-slate-100"
                    >
                      <td className="px-3 py-2 align-top">
                        <Link
                          to={`/admin/workflow-engine/designer/${w.definition_id}`}
                          className="font-semibold text-slate-900 hover:text-blue-700"
                        >
                          {w.name}
                        </Link>
                        <div className="text-[10px] text-slate-500">
                          v{w.version} · {w.state}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-slate-700">
                        {r.step_name || <span className="text-slate-400 italic">—</span>}
                      </td>
                      <td className="px-3 py-2 align-top text-xs">
                        {r.vendor_name || r.service_name ? (
                          <span className="text-slate-800">
                            <strong>{r.vendor_name || "—"}</strong> · {r.service_name || "—"}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">no vendor picked</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <CoverageStatusPill status={r.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* By-vendor rollup */}
          {summary.by_vendor.length > 0 && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="wfe-coverage-by-vendor">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                By vendor
              </div>
              <ul className="flex flex-wrap gap-1.5">
                {summary.by_vendor.map((v) => (
                  <li
                    key={v.vendor_id}
                    data-testid={`coverage-vendor-${v.vendor_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-[11px]"
                  >
                    <span className="font-semibold text-slate-900">{v.vendor_name || "(unknown)"}</span>
                    <span className="text-slate-500">
                      {v.ref_count} ref{v.ref_count === 1 ? "" : "s"} · {v.workflow_count} workflow{v.workflow_count === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}



export default function AdminWorkflowEngine() {
  const [templates, setTemplates] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [events, setEvents] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [drift, setDrift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newWorkflowOpen, setNewWorkflowOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [batchScenariosOpen, setBatchScenariosOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, d, r, e, c, drf] = await Promise.all([
        api.get("/admin/wfe/program-templates"),
        api.get("/admin/wfe/definitions"),
        api.get("/admin/wfe/runs", { params: { limit: 50 } }),
        api.get("/admin/wfe/events", { params: { limit: 30 } }),
        api.get("/admin/wfe/vendor-coverage"),
        api.get("/admin/wfe/vendor-coverage/drift"),
      ]);
      setTemplates(t.data?.templates || []);
      setDefinitions(d.data?.definitions || []);
      setRuns(r.data?.runs || []);
      setEvents(e.data?.events || []);
      setCoverage(c.data || null);
      setDrift(drf.data || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Group definitions by slug so we can see version history at a glance.
  const definitionsBySlug = useMemo(() => {
    const map = {};
    definitions.forEach((d) => {
      if (!map[d.slug]) map[d.slug] = [];
      map[d.slug].push(d);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => (b.version || 0) - (a.version || 0)),
    );
    return map;
  }, [definitions]);

  return (
    <ToolPageShell
      eyebrow="Admin · Operations"
      title="Workflow Engine"
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setNewWorkflowOpen(true)}
            data-testid="wfe-new-workflow"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-3 py-2 text-sm font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> New workflow
          </button>
          <button
            type="button"
            onClick={() => setBatchScenariosOpen(true)}
            data-testid="wfe-run-all-scenarios"
            className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-3 py-2 text-sm font-semibold"
            title="Batch-replay every recorded scenario across all workflows"
          >
            <Play className="w-3.5 h-3.5" /> Run all scenarios
          </button>
          <Link
            to="/admin/workflow-engine/analytics"
            data-testid="wfe-go-analytics"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 rounded-full px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <BarChart3 className="w-3.5 h-3.5" /> Analytics
          </Link>
          <button
            type="button"
            onClick={load}
            data-testid="wfe-refresh"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 rounded-full px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      }
      intro="System of record for workflow orchestration across all Nodera programs. Program templates seed canonical workflow definitions; the execution engine runs them; the analytics page surfaces funnel + bottleneck health."
    >
      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {/* Program templates */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-blue-700" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
            Program Templates ({templates.length})
          </h2>
        </div>
        {templates.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No templates seeded yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wfe-templates-grid">
            {templates.map((t) => {
              // Prefer the current live definition for this slug; fall back
              // to the canonical id if there isn't one deployed yet.
              const live = definitions.find((d) => d.slug === t.slug && d.state === "live");
              const targetId = live?.id || t.canonical_workflow_id;
              return (
              <Link
                key={t.slug}
                to={`/admin/workflow-engine/designer/${targetId}`}
                data-testid={`wfe-template-card-${t.slug}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-blue-400 hover:shadow-sm transition group block"
              >
                <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">{t.category}</div>
                <div className="text-base font-bold text-slate-900 mt-0.5">{t.name}</div>
                <p className="text-xs text-slate-600 mt-1">{t.description}</p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500 font-mono truncate">
                    {live ? `v${live.version} · live` : `slug: ${t.slug}`}
                  </span>
                  <span className="text-[10px] font-semibold text-blue-700 inline-flex items-center gap-0.5 opacity-70 group-hover:opacity-100 shrink-0">
                    Edit workflow →
                  </span>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Workflow definitions */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <WorkflowIcon className="w-4 h-4 text-blue-700" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
            Workflow Definitions ({definitions.length})
          </h2>
        </div>
        {Object.keys(definitionsBySlug).length === 0 ? (
          <div className="text-sm text-slate-500 italic">No definitions.</div>
        ) : (
          <div className="space-y-3" data-testid="wfe-definitions-list">
            {Object.entries(definitionsBySlug).map(([slug, defs]) => (
              <div
                key={slug}
                data-testid={`wfe-definition-group-${slug}`}
                className="rounded-2xl border border-slate-200 bg-white p-4"
              >
                <div className="text-sm font-bold text-slate-900 mb-2">{defs[0].name}</div>
                <ul className="space-y-1">
                  {defs.map((d) => (
                    <li
                      key={d.id}
                      data-testid={`wfe-definition-row-${d.id}`}
                      className="text-xs flex items-center justify-between gap-2 py-1"
                    >
                      <Link
                        to={`/admin/workflow-engine/designer/${d.id}`}
                        className="font-mono text-blue-700 hover:underline"
                      >
                        v{d.version}
                      </Link>
                      <StatePill value={d.state} />
                      <span className="text-slate-500 flex-1 text-right">{fmtDate(d.updated_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vendor coverage map */}
      <VendorCoverageSection coverage={coverage} drift={drift} onOpenBackfill={() => setBackfillOpen(true)} />

      {/* Events (Layer 4) */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-blue-700" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
            Recent Events ({events.length})
          </h2>
        </div>
        {events.length === 0 ? (
          <div
            data-testid="wfe-events-empty"
            className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500"
          >
            No events received yet. Vendor systems POST to{" "}
            <code className="bg-slate-100 rounded px-1 text-xs">/api/webhooks/wfe/{"{event_type}"}</code>{" "}
            to drive workflow advancement.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="wfe-events-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-2">Event</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-right px-3 py-2">Advanced</th>
                  <th className="text-left px-3 py-2">Received</th>
                </tr>
              </thead>
              <tbody>
                {events.slice(0, 30).map((evt) => (
                  <tr
                    key={evt.id}
                    data-testid={`wfe-event-row-${evt.id}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-900">{evt.event_type}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-700">{evt.source || "—"}</td>
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-600">{evt.subject_id || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-right">
                      {(evt.dispatch?.advanced_run_ids || []).length}/{evt.dispatch?.candidates_scanned ?? 0}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{fmtDate(evt.received_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent runs */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-blue-700" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
            Recent Runs ({runs.length})
          </h2>
        </div>
        {runs.length === 0 ? (
          <div
            data-testid="wfe-runs-empty"
            className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500"
          >
            No runs yet. Workflow runs are created when a program template is
            instantiated for a subject (patient, clinic, vendor, employer).
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="wfe-runs-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-2">Workflow</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Sandbox</th>
                  <th className="text-left px-3 py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`wfe-run-row-${r.id}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-3 py-2 font-semibold text-slate-900">{r.workflow_slug} v{r.workflow_definition_version}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {r.subject_type} · <span className="font-mono">{r.subject_id}</span>
                    </td>
                    <td className="px-3 py-2">
                      <StatePill value={r.status} palette={RUN_STATUS_PALETTE} />
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.is_sandbox ? <span className="text-violet-700">sandbox</span> : <span className="text-slate-400">prod</span>}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{fmtDate(r.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewWorkflowModal
        open={newWorkflowOpen}
        onClose={() => setNewWorkflowOpen(false)}
        templates={templates}
      />
      <BackfillVendorsModal
        open={backfillOpen}
        onClose={() => setBackfillOpen(false)}
        onApplied={load}
      />
      <BatchScenariosModal
        open={batchScenariosOpen}
        onClose={() => setBatchScenariosOpen(false)}
      />
    </ToolPageShell>
  );
}
